import React, { useState } from "react";
import { COLORS, HEAD_LABEL, SHAFT_LABEL, PATTERN_LABEL, malletSVG, fmt } from "../lib/mallet";
import { useCart } from "../context/CartContext";

const DEFAULT_CUSTOM = { length: 52, head: "rnpp", pattern: "natural", headColor: "#D9B27A", color2: "#C81DD8", grip: "#F4F1EA", shaft: "natural", text: "", qty: 1 };

function customUnitPrice(custom) {
  let p = 220;
  if (custom.head === "skene") p += 25;
  p += { natural: 0, tips: 20, band: 25, stripes: 35, splash: 60, solid: 30 }[custom.pattern];
  if (custom.shaft === "flamed") p += 15;
  if (custom.shaft === "white") p += 20;
  if (custom.text) p += 15;
  return p;
}

function Swatches({ list, value, onPick }) {
  return (
    <div className="swatches">
      {list.map(([n, h]) => (
        <button key={h} className="swatch" style={{ background: h }} aria-label={n} aria-pressed={value === h} onClick={() => onPick(h)} />
      ))}
    </div>
  );
}

function Seg({ options, value, onPick }) {
  return (
    <div className="seg">
      {options.map(([v, label]) => (
        <button key={v} aria-pressed={value === v} onClick={() => onPick(v)}>{label}</button>
      ))}
    </div>
  );
}

export default function Customizer() {
  const { addToCart } = useCart();
  const [custom, setCustom] = useState(DEFAULT_CUSTOM);
  const set = (k, v) => setCustom(c => ({ ...c, [k]: v }));

  const unit = customUnitPrice(custom);
  const q = custom.qty;
  const disc = q >= 6 ? .9 : 1;
  const total = Math.round(unit * q * disc);
  const weight = 440 + (custom.length - 49) * 14 + (custom.head === "skene" ? 20 : 0);

  const handleAdd = () => {
    const detail = `${custom.length}\" ${HEAD_LABEL[custom.head]}, ${PATTERN_LABEL[custom.pattern]}, ${SHAFT_LABEL[custom.shaft]}${custom.text ? `, engraved \"${custom.text}\"` : ""}`;
    addToCart({ key: "custom-" + JSON.stringify(custom), name: "Custom mallet", detail, price: Math.round(unit * disc), qty: custom.qty, spec: { ...custom } });
  };

  return (
    <section id="customize">
      <div className="wrap">
        <div className="sec-head">
          <h2 style={{ color: "#fff" }}>Design your mallet</h2>
          <p style={{ color: "#BFD7C8" }}>Change anything and watch it update. Add it to the cart when it looks right — we build it to your spec and confirm by phone before we cut cane.</p>
        </div>
        <div className="custom-grid">
          <div className="preview">
            <div dangerouslySetInnerHTML={{ __html: malletSVG(custom) }} />
            <div className="spec">
              <div><b>{custom.length}"</b>Length</div>
              <div><b>{HEAD_LABEL[custom.head]}</b>Head shape</div>
              <div><b>≈ {weight} g</b>Target weight</div>
              <div><b>{PATTERN_LABEL[custom.pattern]}{custom.text ? ", engraved" : ""}</b>Finish</div>
            </div>
          </div>
          <div className="controls">
            <div className="field">
              <label htmlFor="cLength">Length: <span>{custom.length}</span> inches</label>
              <input type="range" id="cLength" min="49" max="54" step="1" value={custom.length} onChange={e => set("length", +e.target.value)} />
              <div className="hint">49"–50" for ponies under 15h, 51"–52" for 15–15.2h, 53"–54" for taller ponies.</div>
            </div>
            <div className="field">
              <span className="lbl">Head shape</span>
              <Seg options={[["cigar", "Cigar"], ["rnpp", "RNPP"], ["skene", "Skene"]]} value={custom.head} onPick={v => set("head", v)} />
            </div>
            <div className="field">
              <span className="lbl">Head finish</span>
              <Seg options={[["natural", "Natural tipa"], ["tips", "Painted tips"], ["band", "Club band"], ["stripes", "Stripes"], ["splash", "Neon splash"], ["solid", "Solid color"]]} value={custom.pattern} onPick={v => set("pattern", v)} />
            </div>
            <div className="field">
              <span className="lbl">Head color</span>
              <Swatches list={COLORS.head} value={custom.headColor} onPick={v => set("headColor", v)} />
            </div>
            <div className="field">
              <span className="lbl">Second color</span>
              <Swatches list={COLORS.second} value={custom.color2} onPick={v => set("color2", v)} />
            </div>
            <div className="field">
              <span className="lbl">Grip</span>
              <Swatches list={COLORS.grip} value={custom.grip} onPick={v => set("grip", v)} />
            </div>
            <div className="field">
              <span className="lbl">Shaft</span>
              <Seg options={[["natural", "Natural cane"], ["flamed", "Flamed"], ["white", "White-dipped shaft"]]} value={custom.shaft} onPick={v => set("shaft", v)} />
            </div>
            <div className="field">
              <label htmlFor="cText">Engraving on the head (up to 14 characters)</label>
              <input className="text-in" id="cText" maxLength="14" placeholder="Player number, initials, club" value={custom.text} onChange={e => set("text", e.target.value)} />
              <div className="hint">Logos and crests: email us the artwork after checkout.</div>
            </div>
            <div className="field">
              <label htmlFor="cQty">How many? Sets of 6 or more get matching numbers and 10% off.</label>
              <input type="range" id="cQty" min="1" max="12" value={custom.qty} onChange={e => set("qty", +e.target.value)} /> <span>{q === 1 ? "1 mallet" : q + " mallets" + (q >= 6 ? " (10% off)" : "")}</span>
            </div>
            <div className="custom-total">
              <div>
                <div style={{ color: "#BFD7C8", fontSize: ".9rem" }}>Your mallet</div>
                <div className="price">{fmt(total)}{q > 1 ? ` (${fmt(unit)} each)` : ""}</div>
              </div>
              <button className="btn btn-primary" onClick={handleAdd}>Add to cart</button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
